import React, { useRef, useEffect } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useStore } from 'zustand';
import { calendarStore, CalendarDTO } from 'entities/calendar';

import { MonthDayCell } from '../MonthDayCell';
import { WeekDayHeader } from '../WeekDayHeader';

import styles from './MonthCell.module.scss';

type MonthCellProps = {
  date: Dayjs;
  events: CalendarDTO['events'];
  onSelect: (date: Dayjs) => void;
  selectedRange: { start: Dayjs | null; end: Dayjs | null };
  isRangeBlocked: (start: Dayjs, end: Dayjs) => boolean;
};

const weekDays = Array.from({ length: 7 }, (_, i) => dayjs().day(i));

const generateMonthDays = (startOfMonth: Dayjs): Dayjs[] =>
  Array.from({ length: startOfMonth.daysInMonth() }, (_, index) => startOfMonth.clone().add(index, 'day'));

const MonthCell = ({ date, events, onSelect, selectedRange, isRangeBlocked }: MonthCellProps): React.ReactElement => {
  const openedEventId = useStore(calendarStore, (state) => state.openedEventId);
  const startOfMonth = date.startOf('month');
  const monthDate = date.format('YYYY-MM');

  const daysInMonth = generateMonthDays(startOfMonth);
  const firstDayOffset = startOfMonth.day();

  const dayRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const isDaySelected = (day: Dayjs): boolean => {
    if (selectedRange && selectedRange.start && selectedRange.end) {
      return day.isBetween(selectedRange.start, selectedRange.end, 'day', '[]');
    }
    if (selectedRange && selectedRange.start && !selectedRange.end) {
      return day.isSame(selectedRange.start, 'day');
    }
    return false;
  };

  useEffect(() => {
    if (!openedEventId?.clickedDate) return;

    const dayRef = dayRefs.current[openedEventId.clickedDate];
    if (dayRef) {
      dayRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [openedEventId]);

  return (
    <div className={styles.monthCellWrapper}>
      <div className={styles.weekDaysWrapper}>
        {weekDays.map((day) => (
          <WeekDayHeader key={`${monthDate}-header-${day.format('dd')}`} day={day} />
        ))}
        {Array.from({ length: firstDayOffset }).map((_, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={`${monthDate}-empty-${index}`} />
        ))}
        {daysInMonth.map((day) => {
          const isPopovered = openedEventId
            ? day.isBetween(dayjs(openedEventId.startDate), dayjs(openedEventId.endDate), 'day', '[]')
            : false;

          const currentEvent = events.find(
            (event) =>
              day.isBetween(dayjs(event.startDate), dayjs(event.endDate), 'day', '[]') &&
              day.format('YYYY-MM') === date.format('YYYY-MM'),
          );

          const isSelected = isDaySelected(day);

          const isBlocked = selectedRange.start ? isRangeBlocked(selectedRange.start, day) : false;

          return (
            <MonthDayCell
              key={`${monthDate}-day-${day.format('DD')}`}
              day={day}
              event={currentEvent}
              isOpened={isPopovered}
              openedEventId={openedEventId}
              onSelect={onSelect}
              isBlocked={isBlocked}
              isSelected={isSelected}
              ref={(el) => {
                if (currentEvent) {
                  dayRefs.current[day.format('YYYY-MM-DD')] = el;
                }
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(MonthCell, (prevProps, nextProps) => {
  const sameDate = prevProps.date.isSame(nextProps.date, 'year');
  const sameLocale = prevProps.date.locale() === nextProps.date.locale();
  const sameStart =
    (!prevProps.selectedRange?.start && !nextProps.selectedRange?.start) ||
    (prevProps.selectedRange?.start && nextProps.selectedRange?.start
      ? prevProps.selectedRange.start.isSame(nextProps.selectedRange.start, 'day')
      : false);

  const sameEnd =
    (!prevProps.selectedRange?.end && !nextProps.selectedRange?.end) ||
    (prevProps.selectedRange?.end && nextProps.selectedRange?.end
      ? prevProps.selectedRange.end.isSame(nextProps.selectedRange.end, 'day')
      : false);

  const sameEvents =
    prevProps.events.length === nextProps.events.length &&
    prevProps.events.every((event, index) => {
      const nextEvent = nextProps.events[index];
      if (!nextEvent) return false;

      return (
        event.id === nextEvent.id &&
        event.name === nextEvent.name &&
        event.startDate === nextEvent.startDate &&
        event.endDate === nextEvent.endDate &&
        event.exclusionType === nextEvent.exclusionType &&
        event.workingHours === nextEvent.workingHours
      );
    });

  return sameDate && sameLocale && sameStart && sameEnd && sameEvents;
});
